var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import useSWRMutation from "swr/mutation";
import { LogLevel } from "../../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../../Shared/hooks/useLogging/useLogging";
import { AUTH_SWR_KEYS } from "../Utilities/swrKeys";
import { addUpdateAddress, buildOptimisticAddressData, processAddressError } from "./addressMutations-utils";
import { useCachedSWR } from "../Utilities/useCachedSWR";
/**
 * SWR mutation hook for adding or updating a delivery address.
 *
 * POST /Account/AddUpdateAccountDeliveryAddress
 *
 * The backend determines whether to create or update based on addressId:
 * - addressId === 0: Create new address
 * - addressId > 0: Update existing address
 *
 * Cache update strategy:
 * - `optimisticData`: instantly updates the UI via buildOptimisticAddressData before the API responds.
 * - `rollbackOnError`: reverts to the previous cache state if the API call fails.
 * - `populateCache`: merges the server response into the cache after the API responds.
 * - `revalidate: false`: skips the redundant GET after mutation since the cache is already correct.
 */
export const useAddUpdateAddressMutation = () => {
    const { datadogLog } = useLogging();
    const { data: currentData } = useCachedSWR(AUTH_SWR_KEYS.DELIVERY_ADDRESSES);
    const { trigger: triggerMutation } = useSWRMutation(AUTH_SWR_KEYS.DELIVERY_ADDRESSES, addUpdateAddress, {
        onError: (error) => {
            const processedError = processAddressError(error);
            datadogLog("Failed to add/update address.", LogLevel.ERROR, undefined, processedError);
        },
        revalidate: false,
    });
    const trigger = (address) => __awaiter(void 0, void 0, void 0, function* () {
        return triggerMutation({ address }, {
            optimisticData: buildOptimisticAddressData(currentData, address),
            rollbackOnError: true,
            // populateCache receives the ORIGINAL (pre-optimistic) cache data, not the optimistic data.
            // So we must merge the address (with real server ID) into the original data from scratch.
            populateCache: (result, originalData) => {
                const addressWithRealId = Object.assign(Object.assign({}, address), { addressId: result.addressId });
                if (!originalData)
                    return { success: true, data: [addressWithRealId], pickupLocations: [], usesLocation: false, usesZipCodes: false };
                const isUpdate = address.addressId > 0;
                const updatedAddresses = isUpdate
                    ? originalData.data.map((addr) => (addr.addressId === address.addressId ? addressWithRealId : addr))
                    : [...originalData.data, addressWithRealId];
                return Object.assign(Object.assign({}, originalData), { data: updatedAddresses });
            },
        });
    });
    return { trigger };
};
